import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Grid3x3, Wrench, Tag, Calendar, Users, LogOut, Settings, Wallet, Award, UserCircle, Share2, Building2, Receipt, ClipboardCheck, BookOpen, FileBarChart, Landmark, Percent, ShieldCheck, X, KeyRound, Loader2, Check, UploadCloud } from 'lucide-react';
import axios from 'axios';
import { ASSETS } from '../mock/data';
import { useApp } from '../context/AppContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const token = localStorage.getItem('c24_token') || localStorage.getItem('c24_admin_token');
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const nav = [
  { to: '/admin',              label: 'Dashboard',   icon: LayoutDashboard, end: true, testid: 'admin-nav-dashboard', tab: 'admin_dashboard' },
  { to: '/admin/erp',          label: 'Master ERP',  icon: Receipt,                     testid: 'admin-nav-erp',         tab: 'admin_erp' },
  { to: '/admin/accounts',     label: 'Accounts',    icon: BookOpen,                    testid: 'admin-nav-accounts',    tab: 'admin_accounts' },
  { to: '/admin/reports',      label: 'Reports',     icon: FileBarChart,                testid: 'admin-nav-reports',     tab: 'admin_reports' },
  { to: '/admin/tax',          label: 'Tax & GST',   icon: Percent,                     testid: 'admin-nav-tax',         tab: 'admin_tax' },
  { to: '/ho',                 label: 'HO Portal',   icon: Landmark,                    testid: 'admin-nav-ho',          tab: 'admin_ho' },
  { to: '/admin/approvals',    label: 'Approvals',   icon: ClipboardCheck,              testid: 'admin-nav-approvals',   tab: 'admin_approvals' },
  { to: '/admin/franchises',   label: 'Franchises',  icon: Building2,                   testid: 'admin-nav-franchises',  tab: 'admin_franchises' },
  { to: '/admin/permissions',  label: 'Permissions', icon: ShieldCheck,                 testid: 'admin-nav-permissions', tab: 'admin_permissions' },
  { to: '/admin/bookings',     label: 'Bookings',    icon: Calendar,                    testid: 'admin-nav-bookings',    tab: 'admin_bookings' },
  { to: '/admin/users',        label: 'User CRM',    icon: UserCircle,                  testid: 'admin-nav-users',       tab: 'admin_user_crm' },
  { to: '/admin/import-users', label: 'Import Users',icon: UploadCloud,                 testid: 'admin-nav-import-users',tab: 'admin_import_users' },
  { to: '/admin/workers',      label: 'Workers',     icon: Users,                       testid: 'admin-nav-workers',     tab: 'admin_workers' },
  { to: '/admin/wallet',       label: 'Wallet',      icon: Wallet,                      testid: 'admin-nav-wallet',      tab: 'admin_wallet' },
  { to: '/admin/rewards',      label: 'Rewards',     icon: Award,                       testid: 'admin-nav-rewards',     tab: 'admin_rewards' },
  { to: '/admin/referrals',    label: 'Referrals',   icon: Share2,                      testid: 'admin-nav-referrals',   tab: 'admin_referrals' },
  { to: '/admin/categories',   label: 'Categories',  icon: Grid3x3,                     testid: 'admin-nav-categories',  tab: 'admin_categories' },
  { to: '/admin/services',     label: 'Services',    icon: Wrench,                      testid: 'admin-nav-services',    tab: 'admin_services' },
  { to: '/admin/offers',       label: 'Offers',      icon: Tag,                         testid: 'admin-nav-offers',      tab: 'admin_offers' },
  { to: '/admin/settings',     label: 'Global Settings', icon: Settings,                testid: 'admin-nav-settings',    tab: 'admin_settings' },
];

const AdminLayout = ({ children, title }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, user } = useApp();
  const [showProfile, setShowProfile] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('c24_admin');
    navigate('/admin/login');
  };

  const visibleNav = nav.filter((item) => hasPermission(item.tab, 'view'));

  return (
    <div className="min-h-screen bg-neutral-100 flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 bg-neutral-900 text-neutral-300 min-h-screen flex-col">
        <div className="p-5 border-b border-neutral-800">
          <div className="bg-white rounded-lg p-2 inline-block">
            <img src={ASSETS.logo} alt="Clean24" className="h-12" />
          </div>
          <p className="mt-3 text-xs text-neutral-500 uppercase tracking-wider font-bold">Super Admin</p>
          <button
            data-testid="admin-nav-my-profile"
            onClick={() => setShowProfile(true)}
            className="mt-3 w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-neutral-100 text-xs font-semibold transition-colors">
            <UserCircle className="w-4 h-4 text-[#2E7D32]" />
            <span className="truncate flex-1 text-left">{user?.name || user?.email || 'My Profile'}</span>
            <KeyRound className="w-3 h-3 text-neutral-500" />
          </button>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {visibleNav.map(({ to, label, icon: Icon, end, testid }) => (
            <NavLink key={to} to={to} end={end} data-testid={testid}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-[#2E7D32] text-white' : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'}`}>
              <Icon className="w-4 h-4" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-neutral-800 space-y-1">
          <button onClick={() => navigate('/')} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-neutral-300 hover:bg-neutral-800 hover:text-white transition-colors">
            <Settings className="w-4 h-4" /> View Site
          </button>
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-400 hover:bg-red-500/10 transition-colors">
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </aside>

      {/* Mobile top nav */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-neutral-900 text-white z-40">
        <div className="px-4 py-3 flex items-center gap-3">
          <div className="bg-white rounded p-1">
            <img src={ASSETS.logo} className="h-8" alt="" />
          </div>
          <p className="font-bold text-sm">Admin Panel</p>
          <button
            data-testid="admin-nav-my-profile-mobile"
            onClick={() => setShowProfile(true)}
            className="ml-auto inline-flex items-center gap-1.5 bg-neutral-800 hover:bg-neutral-700 px-3 py-1.5 rounded-full text-xs font-semibold">
            <UserCircle className="w-4 h-4 text-[#2E7D32]" /> My Profile
          </button>
          <button onClick={handleLogout} className="text-red-400 text-xs">Log out</button>
        </div>
        <div className="flex gap-1 px-3 pb-2 overflow-x-auto scrollbar-hide">
          {nav.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end}
              className={({ isActive }) => `flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${isActive ? 'bg-[#2E7D32] text-white' : 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700'}`}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </NavLink>
          ))}
        </div>
      </div>

      {/* Main content */}
      <main className="flex-1 min-w-0 pt-24 md:pt-0">
        <div className="p-5 md:p-8 max-w-[1400px] mx-auto">
          {title && <h1 className="text-2xl md:text-3xl font-extrabold text-neutral-900 mb-6">{title}</h1>}
          {children}
        </div>
      </main>

      {showProfile && <MyProfileModal user={user} onClose={() => setShowProfile(false)} />}
    </div>
  );
};

const MyProfileModal = ({ user, onClose }) => {
  const [form, setForm] = useState({ old_password: '', new_password: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [show, setShow] = useState({ old: false, new: false });

  const submit = async (e) => {
    e?.preventDefault?.();
    setMsg(null);
    if (!form.old_password) return setMsg({ ok: false, text: 'Current password is required' });
    if ((form.new_password || '').length < 6) return setMsg({ ok: false, text: 'New password must be at least 6 characters' });
    if (form.new_password !== form.confirm) return setMsg({ ok: false, text: 'New password and confirmation do not match' });
    if (form.old_password === form.new_password) return setMsg({ ok: false, text: 'New password must differ from current password' });
    setBusy(true);
    try {
      await axios.post(`${API}/me/change-password`,
        { old_password: form.old_password, new_password: form.new_password },
        authCfg());
      setMsg({ ok: true, text: 'Password updated. Please use your new password from now on.' });
      setForm({ old_password: '', new_password: '', confirm: '' });
    } catch (err) {
      setMsg({ ok: false, text: err?.response?.data?.detail || 'Failed to update password' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="my-profile-modal" className="bg-white rounded-2xl w-full max-w-md">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-neutral-100">
          <UserCircle className="w-5 h-5 text-[#2E7D32]" />
          <div className="flex-1 min-w-0">
            <p className="font-extrabold text-sm">My Profile</p>
            <p className="text-[11px] text-neutral-500 truncate">{user?.email || '—'} · {user?.role || 'admin'}</p>
          </div>
          <button data-testid="my-profile-close" onClick={onClose} className="text-neutral-400 hover:text-neutral-900"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-3">
          <p className="text-xs text-neutral-600 flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5 text-[#EA580C]" /> Change your password. Passwords are never displayed anywhere.</p>

          <div>
            <label className="block text-xs font-semibold text-neutral-700 mb-1">Current password</label>
            <div className="relative">
              <input
                data-testid="my-profile-old-password"
                type={show.old ? 'text' : 'password'}
                value={form.old_password}
                onChange={(e) => setForm({ ...form, old_password: e.target.value })}
                autoComplete="current-password"
                className="w-full px-3 py-2.5 pr-16 rounded-lg border border-neutral-200 text-sm font-mono focus:outline-none focus:border-[#2E7D32]"
                placeholder="Enter current password" required />
              <button type="button" onClick={() => setShow({ ...show, old: !show.old })}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] font-semibold text-neutral-500 hover:text-neutral-900">
                {show.old ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-neutral-700 mb-1">New password <span className="text-neutral-400 font-normal">(min 6 chars)</span></label>
            <div className="relative">
              <input
                data-testid="my-profile-new-password"
                type={show.new ? 'text' : 'password'}
                value={form.new_password}
                onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                autoComplete="new-password"
                className="w-full px-3 py-2.5 pr-16 rounded-lg border border-neutral-200 text-sm font-mono focus:outline-none focus:border-[#2E7D32]"
                placeholder="Enter new password" required />
              <button type="button" onClick={() => setShow({ ...show, new: !show.new })}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] font-semibold text-neutral-500 hover:text-neutral-900">
                {show.new ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-neutral-700 mb-1">Confirm new password</label>
            <input
              data-testid="my-profile-confirm-password"
              type={show.new ? 'text' : 'password'}
              value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              autoComplete="new-password"
              className="w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm font-mono focus:outline-none focus:border-[#2E7D32]"
              placeholder="Re-enter new password" required />
          </div>

          {msg && (
            <div data-testid="my-profile-msg"
              className={`text-xs rounded-lg px-3 py-2 flex items-start gap-1.5 ${msg.ok ? 'bg-[#E8F5E9] text-[#1B5E20]' : 'bg-red-50 text-red-700'}`}>
              {msg.ok && <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />}
              <span>{msg.text}</span>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-lg border border-neutral-200 text-sm font-semibold text-neutral-700 hover:bg-neutral-50">
              Cancel
            </button>
            <button
              data-testid="my-profile-submit"
              type="submit" disabled={busy}
              className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-[#2E7D32] hover:bg-[#276D2A] disabled:opacity-60 text-white text-sm font-semibold">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
              {busy ? 'Updating…' : 'Update password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminLayout;
